import { useState } from 'react';

import type { UseLoadMoreOrPreviousOptions } from '../components/types';
import { dispatchPaginationClickEvent } from '../utils/tracking';

interface GetPrevPageOffsetOptions {
  currentOffset: number;
  articlesLength: number;
  pageSize: number;
}

const getPrevPageOffset = ({
  currentOffset,
  articlesLength,
  pageSize,
}: GetPrevPageOffsetOptions) => {
  const numberOfFetchedPages = Math.ceil(articlesLength / pageSize);

  return currentOffset - (numberOfFetchedPages + 1) * pageSize;
};

const countFetchingArticles = ({
  articlesToFetch,
  isLoadingMore,
  isLoadingPrev,
  pageSize,
}: {
  articlesToFetch: number;
  isLoadingMore: boolean;
  isLoadingPrev: boolean;
  pageSize: number;
}) => articlesToFetch || (isLoadingMore || isLoadingPrev ? pageSize : 0);

export const useLoadMoreOrPrevious = ({
  articles,
  appendArticles,
  prependArticles,
  offset,
  fetchProducts,
  articlesToFetch,
  page,
  setPage,
  minLoadedPage,
  maxLoadedPage,
  setMinLoadedPage,
  setMaxLoadedPage,
  pageSize,
  onCurrentDisplayPageChange,
}: UseLoadMoreOrPreviousOptions) => {
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoadingPrev, setIsLoadingPrev] = useState(false);
  const [currentDisplayPage, setCurrentDisplayPage] = useState(maxLoadedPage);

  const prevPageOffset = getPrevPageOffset({
    currentOffset: offset,
    articlesLength: articles.length,
    pageSize,
  });

  const fetchPrevProducts = async () => {
    setIsLoadingPrev(true);
    dispatchPaginationClickEvent({ page, previous: true });
    try {
      const loadedArticles = await fetchProducts(prevPageOffset);
      prependArticles(loadedArticles);
      const newMinPage = minLoadedPage - 1;
      setMinLoadedPage(newMinPage);
      setCurrentDisplayPage(newMinPage);
      onCurrentDisplayPageChange(newMinPage);
    } catch {
      // Ignore error - no UX behavior for that
    }
    setIsLoadingPrev(false);
  };

  const fetchMoreProducts = async () => {
    setIsLoadingMore(true);
    dispatchPaginationClickEvent({ page });
    try {
      const loadedArticles = await fetchProducts(offset);
      appendArticles(loadedArticles);
      const newPage = page + 1;
      setPage(newPage);
      setMaxLoadedPage(newPage);
      setCurrentDisplayPage(newPage);
      onCurrentDisplayPageChange(newPage);
    } catch {
      // Ignore error - no UX behavior for that
    }
    setIsLoadingMore(false);
  };

  return {
    fetchPrevProducts,
    fetchMoreProducts,
    fetchingArticlesCount: countFetchingArticles({
      articlesToFetch,
      isLoadingMore,
      isLoadingPrev,
      pageSize,
    }),
    isFetchingPrev: isLoadingPrev,
    prevPageOffset,
    currentDisplayPage,
  };
};
